import { useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import { FormikErrors, useFormik } from 'formik';
import {
  UiModal,
  UiText,
  UiTitle,
  UiInput,
  UiButton,
} from '@frontend/znanie-uikit';
import { useCurrentUser } from 'hooks/user/useCurrentUser';

import InfoMessage from 'components/common/InfoMessage';

import { useMediaQueries } from 'hooks/utils/useMediaQueries';

import * as T from './ExitAttempt.types';
import * as C from './ExitAttempt.const';

import SmileImage from './img/smile.png';

import { trackAnalyticsData, AnalyticsEvent } from 'utils/analytics';
import { validateEmail, validateSpaces } from 'utils/helpers';
import {
  invalidEmailFormatText,
  notOnlySpacesText,
  requiredText,
} from 'utils/constants';

import css from './ExitAttempt.module.scss';

export default function ExitAttempt() {
  const [visible, setVisible] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { isDesktop } = useMediaQueries();
  const userEmail = useCurrentUser().data?.me?.email;

  //пользователь уже отправлял форму в аналитику
  const hasExitAttempt = (): boolean => !!localStorage.getItem(C.STORAGE_KEY);

  const saveExitAttempt = (): void =>
    localStorage.setItem(C.STORAGE_KEY, String(true));

  const onClose = (): void => {
    setVisible(false);
    saveExitAttempt();
  };

  const formik = useFormik<T.IForm>({
    initialValues: {
      email: '',
    },
    validate: (values) => {
      const errors: FormikErrors<T.IForm> = {};

      if (!values.email) {
        errors.email = requiredText;
      } else if (!validateSpaces(values.email)) {
        errors.email = notOnlySpacesText;
      } else if (!validateEmail(values.email)) {
        errors.email = invalidEmailFormatText;
      }

      return errors;
    },
    onSubmit: () => {
      try {
        if (!hasExitAttempt()) {
          trackAnalyticsData(AnalyticsEvent.EXIT_ATTEMPT, {
            email: values.email,
          });
          setSuccess(true);
          saveExitAttempt();
        }
      } catch (err) {
        console.log(err);
      }
    },
  });

  const { handleSubmit, handleChange, handleBlur, values, touched, errors } =
    formik;

  const checkBrowserVisibilityTab = (): void => {
    document.visibilityState !== 'hidden' && !hasExitAttempt()
      ? setVisible(true)
      : setVisible(false);
  };
  const checkBrowserBlur = (e: FocusEvent): void => {
    e.stopPropagation();
    !visible && !hasExitAttempt() ? setVisible(true) : setVisible(false);
  };

  useEffect(() => {
    if (isDesktop && !hasExitAttempt()) {
      document.addEventListener('visibilitychange', checkBrowserVisibilityTab);
      window.addEventListener('blur', checkBrowserBlur);

      return () => {
        document.removeEventListener(
          'visibilitychange',
          checkBrowserVisibilityTab
        );
        window.removeEventListener('blur', checkBrowserBlur);
      };
    }
  }, []);

  useEffect(() => {
    !isDesktop && setVisible(false);
  }, [isDesktop]);

  return isDesktop ? (
    <UiModal isShowing={visible && !userEmail} onClose={onClose}>
      <div className={css.layout}>
        {!success ? (
          <>
            <UiTitle as="h3" size="large">
              Уже уходите?
            </UiTitle>
            <UiText className={css.description} color="grey" size="small_plus">
              На нашем сайте постоянно выходят новые просветительские материалы.
              Оставьте свой e-mail и мы будем вас оповещать о них. Обещаем,
              будет интересно!
            </UiText>
            <form onSubmit={handleSubmit} className={css.form}>
              <UiInput
                id="email"
                name="email"
                tabIndex={1}
                placeholder="Email"
                fluid
                maxLength={255}
                value={values.email}
                onBlur={handleBlur}
                onChange={handleChange}
                errorLabel={
                  touched.email && errors.email ? errors.email : undefined
                }
              />
              <UiButton
                type="submit"
                className={css.btn}
                fluid
                variant="primary"
                tabIndex={2}
              >
                Отправить
              </UiButton>
            </form>
            <div className={css.smileIcon}>
              <Image src={SmileImage} objectFit="contain" />
            </div>
          </>
        ) : (
          <InfoMessage iconName="success" text="До встречи!" />
        )}
      </div>
    </UiModal>
  ) : null;
}
